import {
  BoltIcon,
  Box,
  BriefcaseIcon,
  Card,
  Circle,
  ClockIcon,
  DatesDisplay,
  DocumentTextIcon,
  Flex,
  Image,
  LocationDisplay,
  Text,
  UserAvatar,
} from 'components'
import { forceLight } from 'utils'

import { generatePath, useNavigate } from 'react-router'
import { useStorageUrlForWorkspace, useUser, useWorkspaceRecord } from 'utils'
import defaultImage from '../../../assets/event_tile.png'
import { EventRecord, getDaysAway, getEventImagePath } from '../../../utils/eventUtils'
import { RoutePath } from '../../../utils/routing'

interface Props extends EventRecord {
  isUpcoming?: boolean
  isLive?: boolean
  showWorkspace?: boolean
}

export default function EventCard(event: Props) {
  const navigate = useNavigate()
  const daysAway = getDaysAway(event.startTime!)
  const eventManager = useUser(event.managerId)
  const imageUrl = useStorageUrlForWorkspace(
    event.workspaceId,
    event.id && event.hasImage ? getEventImagePath(event.id) : undefined
  )?.url
  const workspace = useWorkspaceRecord(event.workspaceId)
  const sessionCount = event.sessionCount ?? 0

  function goToEvent(id: string, workspaceId: string) {
    navigate(generatePath(RoutePath.EventInfo, { workspaceId: workspaceId!, id }))
  }

  return (
    <Box
      {...forceLight}
      borderRadius="xl"
      boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.10)"
      overflow="hidden"
    >
      <Card
        cursor="pointer"
        onClick={() => goToEvent(event.id, event.workspaceId)}
        header={
          <Image
            objectFit="cover"
            height="125px"
            src={imageUrl || defaultImage}
            alt={event.title}
            draggable="false"
          />
        }
        body={
          <Flex direction="column" gap={1} overflow="hidden">
            {event.title ? (
              <Text textStyle="bodyLarge" truncate textOverflow="ellipsis">
                {event.title}
              </Text>
            ) : (
              <Text textStyle="bodyLarge" as="i" color="gray">
                Event title
              </Text>
            )}
            <Flex justify="space-between" gap={3}>
              <Flex direction="column" overflow="hidden" gap={1}>
                {event.showWorkspace && (
                  <Flex alignItems="center" gap={1}>
                    <BriefcaseIcon size="sm" type="outline" />
                    <Text textStyle="bodyXSmall" opacity={0.7} truncate textOverflow="ellipsis">
                      {workspace?.title}
                    </Text>
                  </Flex>
                )}

                <DatesDisplay
                  startTime={event.startTime}
                  endTime={event.endTime}
                  iconProps={{
                    color: event.startTime ? 'rgba(23, 23, 23, 1)' : 'rgba(137, 137, 137, 1)',
                  }}
                />
                <LocationDisplay location={event.location} />
              </Flex>
              <Flex align="center">
                <UserAvatar user={eventManager} />
              </Flex>
            </Flex>
          </Flex>
        }
        bodyProps={{ pb: 3 }}
        footer={
          event.isLive ? (
            <Flex direction="row" alignItems="center" gap={2}>
              <Circle size="15px" bg="rgba(255, 255, 255, 0.27)" animation="livePulse">
                <Circle size="5px" bg="white"></Circle>
              </Circle>
              <Text textStyle="bodySmall" color={'white'}>
                Live • {sessionCount.toLocaleString()} {sessionCount === 1 ? 'Session' : 'Sessions'}
              </Text>
            </Flex>
          ) : event.isUpcoming ? (
            <Flex direction="row" alignItems="center" gap={2}>
              {daysAway ? (
                <ClockIcon type="outline" />
              ) : (
                <DocumentTextIcon color={'rgba(137, 137, 137, 1)'} />
              )}
              <Text
                textStyle="bodySmall"
                color={daysAway ? 'rgba(23, 23, 23, 1)' : 'rgba(137, 137, 137, 1)'}
              >
                {daysAway ? daysAway : 'Draft'}
              </Text>
            </Flex>
          ) : (
            <Flex direction="row" alignItems="center" gap={2}>
              <BoltIcon fontSize="md" />
              <Text textStyle="bodySmall">{sessionCount?.toLocaleString() || 0} Sessions</Text>
            </Flex>
          )
        }
        footerProps={{
          display: 'flex',
          bg: event.isLive ? 'rgba(30, 166, 68, 1)' : 'rgba(137, 137, 137, 0.05)',
          p: 3,
          pl: 4,
          backdropFilter: 'blur(40px)',
          boxShadow: '0px 7px 16px 0px rgba(0, 0, 0, 0.08)',
        }}
      />
    </Box>
  )
}
